import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { resources as resourcesData, getCategories } from '@/data/resources';
import { ResourceCard } from '@/components/ResourceCard';
import EmailGateForm from '@/components/resources/EmailGateForm';
import { useToast } from '@/components/ui/use-toast';
import { Search, Mail, RefreshCw } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import FAQSection from '@/components/seo/FAQSection';
import BreadcrumbJsonLd from '@/components/seo/BreadcrumbJsonLd';
import { RESOURCES_HUB_FAQS } from '@/components/seo/faqs';

const LEAD_STORAGE_KEY = 'educ4te.resource-lead';

/**
 * Resources Page
 * Magic-link delivery flow: lead form -> email link -> click -> PDF.
 * No OAuth. Lead data cached in sessionStorage for subsequent downloads
 * within the same browser session.
 * Last updated: 2026-05-26
 */
export default function ResourcesPage() {
  const [filter, setFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [requesting, setRequesting] = useState(null);
  const [lead, setLead] = useState(null);
  const [gateOpen, setGateOpen] = useState(false);
  const [gateResourceId, setGateResourceId] = useState(null);

  const { toast } = useToast();
  const categories = getCategories();

  // Hydrate lead from sessionStorage on mount
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(LEAD_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.email && parsed.name) {
          setLead(parsed);
        }
      }
    } catch {
      // ignore parse errors
    }
  }, []);

  // Filter + search
  const filteredResources = resourcesData.filter((resource) => {
    const matchesCategory = filter === 'All' || resource.category === filter;
    const matchesSearch =
      searchQuery === '' ||
      resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const persistLead = (leadData) => {
    try {
      sessionStorage.setItem(LEAD_STORAGE_KEY, JSON.stringify(leadData));
    } catch {
      // sessionStorage may be unavailable (private mode); fail silently
    }
    setLead(leadData);
  };

  const clearLead = () => {
    try {
      sessionStorage.removeItem(LEAD_STORAGE_KEY);
    } catch {
      // ignore
    }
    setLead(null);
    toast({
      title: 'Cleared',
      description: 'We\'ll ask for your details again on the next download.'
    });
  };

  const requestLinkWithLead = async (resourceId, leadData) => {
    setRequesting(resourceId);
    try {
      const response = await fetch('/resources/request-download', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: leadData.email,
          name: leadData.name,
          organisation: leadData.organisation || undefined,
          resourceId
        })
      });

      if (response.ok) {
        toast({
          title: 'Check your inbox',
          description: 'Your download link is on its way. It will expire in 15 minutes.'
        });
        return;
      }

      let message = 'Something went wrong sending the link. Please try again or email contact@educ4te.com.';
      try {
        const data = await response.json();
        if (data?.error && typeof data.error === 'string') message = data.error;
        else if (data?.message && typeof data.message === 'string') message = data.message;
      } catch {
        // ignore
      }

      if (response.status === 400 || response.status === 429) {
        toast({ title: 'We couldn\'t send your link', description: message, variant: 'destructive' });
      } else {
        toast({
          title: 'Something went wrong',
          description: 'Something went wrong sending the link. Please try again or email contact@educ4te.com.',
          variant: 'destructive'
        });
      }
    } catch (err) {
      console.error('[ResourcesPage] request-download error:', err);
      toast({
        title: 'Something went wrong',
        description: 'Something went wrong sending the link. Please try again or email contact@educ4te.com.',
        variant: 'destructive'
      });
    } finally {
      setRequesting(null);
    }
  };

  const handleRequestLink = (resourceId) => {
    if (lead && lead.email && lead.name) {
      requestLinkWithLead(resourceId, lead);
      return;
    }
    setGateResourceId(resourceId);
    setGateOpen(true);
  };

  const handleGateSuccess = (leadData) => {
    persistLead(leadData);
    // Backend already sent the email for this resource via the modal POST.
    // No second request needed.
  };

  const gateResource = gateResourceId
    ? resourcesData.find((r) => r.id === gateResourceId)
    : null;

  return (
    <div className="min-h-screen bg-white pt-24 pb-20">
      <Helmet>
        <title>Resource Library — Cybersecurity Ebooks, Templates &amp; Tools | EDUC4TE</title>
        <meta name="description" content="Free practitioner resources for Australian IT and security teams — Microsoft 365, Copilot governance, and Essential Eight ebooks, templates, and assessment tools." />
        <link rel="canonical" href="https://educ4te.com/resources" />
        <meta property="og:title" content="Resource Library | EDUC4TE" />
        <meta property="og:description" content="Free cybersecurity ebooks, templates, and tools for Australian organisations." />
        <meta property="og:url" content="https://educ4te.com/resources" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_AU" />
      </Helmet>
      <BreadcrumbJsonLd items={[
        { name: 'Home', url: 'https://educ4te.com/' },
        { name: 'Resources', url: 'https://educ4te.com/resources' },
      ]} />
      <div className="container mx-auto px-4 relative">
        {/* Lead status pill (when cached) */}
        {lead && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="absolute top-4 right-4 flex items-center gap-2 bg-white rounded-full px-3 py-1.5 shadow-md border border-gray-100 z-10 text-xs md:text-sm"
          >
            <Mail size={14} className="text-[#422680]" />
            <span className="hidden sm:inline text-gray-700">Links go to <strong>{lead.email}</strong></span>
            <span className="sm:hidden text-gray-700 truncate max-w-[140px]">{lead.email}</span>
            <button
              type="button"
              onClick={clearLead}
              className="text-gray-500 hover:text-[#F54952] transition-colors flex items-center gap-1"
              title="Use a different email"
            >
              <RefreshCw size={12} />
              <span className="hidden md:inline">Use a different email</span>
            </button>
          </motion.div>
        )}

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12 max-w-4xl mx-auto"
        >
          <h1 className="text-3xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-[#422680] via-[#660F56] to-[#F54952] bg-clip-text text-transparent">
              Resource Library
            </span>
          </h1>
          <p className="text-base md:text-xl text-gray-600 max-w-2xl mx-auto">
            Templates, guides, checklists and presentations to accelerate your digital transformation journey.
          </p>
          <p className="text-sm text-gray-500 mt-3 max-w-xl mx-auto">
            Enter your email and we'll send a one-time download link — valid for 15 minutes.
          </p>
          <div className="mt-4">
            <a href="/terms-of-service" className="text-sm text-[#422680] hover:text-[#F54952] underline mr-4">Terms of Service</a>
            <a href="/privacy-policy" className="text-sm text-[#422680] hover:text-[#F54952] underline">Privacy Policy</a>
          </div>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="max-w-md mx-auto mb-8"
        >
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search resources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#422680] focus:border-transparent"
            />
          </div>
        </motion.div>

        {/* Filter Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex flex-col sm:flex-row justify-center gap-4 mb-12"
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setFilter(category)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                filter === category
                  ? 'bg-[#422680] text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </motion.div>

        {/* Resource Count */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center text-gray-600 mb-8"
        >
          {filteredResources.length} {filteredResources.length === 1 ? 'resource' : 'resources'} available
        </motion.p>

        {/* Resource Grid */}
        {filteredResources.length > 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filteredResources.map((resource) => (
              <ResourceCard
                key={resource.id}
                resource={resource}
                onDownload={handleRequestLink}
                isDownloading={requesting === resource.id}
              />
            ))}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-center py-16"
          >
            <p className="text-gray-600 text-lg">No resources found matching your criteria.</p>
            <button
              onClick={() => {
                setFilter('All');
                setSearchQuery('');
              }}
              className="mt-4 text-[#422680] hover:underline"
            >
              Clear filters
            </button>
          </motion.div>
        )}
      </div>

      <FAQSection faqs={RESOURCES_HUB_FAQS} />

      {/* Related from Educ4te — in-body cross-links for AEO/GEO */}
      <section className="py-12 px-6 bg-white border-t border-[#E8E0F0]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl font-bold text-[#341671] mb-6">Related from Educ4te</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <Link to="/copilot-readiness" className="block p-5 rounded-xl border border-[#E8E0F0] bg-[#F4F0FA] hover:bg-[#EDE8F5] transition-colors">
              <h3 className="text-base font-semibold text-[#341671] mb-2">Copilot AI Readiness Assessment</h3>
              <p className="text-sm text-[#6B5E82] leading-relaxed">Free scored assessment across eight Copilot readiness domains.</p>
            </Link>
            <Link to="/blog" className="block p-5 rounded-xl border border-[#E8E0F0] bg-[#F4F0FA] hover:bg-[#EDE8F5] transition-colors">
              <h3 className="text-base font-semibold text-[#341671] mb-2">Educ4te blog</h3>
              <p className="text-sm text-[#6B5E82] leading-relaxed">Practitioner articles on Microsoft 365 security, Copilot governance, and Essential Eight.</p>
            </Link>
            <Link to="/training" className="block p-5 rounded-xl border border-[#E8E0F0] bg-[#F4F0FA] hover:bg-[#EDE8F5] transition-colors">
              <h3 className="text-base font-semibold text-[#341671] mb-2">Training catalogue</h3>
              <p className="text-sm text-[#6B5E82] leading-relaxed">Australian-aligned cybersecurity and Microsoft 365 certification training.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Email gate modal */}
      <EmailGateForm
        open={gateOpen}
        resourceTitle={gateResource?.title || 'this resource'}
        resourceId={gateResourceId || ''}
        onClose={() => {
          setGateOpen(false);
          setGateResourceId(null);
        }}
        onSuccess={handleGateSuccess}
      />
    </div>
  );
}
